import { useState, useMemo } from 'react'
import { router } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { useBasePath } from '@/lib/paths'
import { CalendarDays, Clock, MapPin, Video, User, ArrowRight } from 'lucide-react'
import type { AvailableSlot } from '@/types'

type NotaryInfo = {
  id: string
  full_name: string
  commission_state: string
  bio: string | null
  session_modes: string[]
}

type Props = {
  notarization: { id: string; type_name: string; business_name: string; session_mode: string }
  notaries: NotaryInfo[]
  available_slots: AvailableSlot[]
}

export default function AppointmentNew({ notarization, notaries, available_slots }: Props) {
  const basePath = useBasePath()
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined)
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Get unique dates that have available slots
  const availableDates = useMemo(() => {
    const dates = new Set<string>()
    available_slots.forEach(slot => {
      const date = new Date(slot.scheduled_at).toDateString()
      dates.add(date)
    })
    return dates
  }, [available_slots])

  // Filter slots for the selected date
  const slotsForDate = useMemo(() => {
    if (!selectedDate) return []
    return available_slots.filter(slot => {
      const slotDate = new Date(slot.scheduled_at)
      return slotDate.toDateString() === selectedDate.toDateString()
    })
  }, [available_slots, selectedDate])

  // Group slots by notary
  const slotsByNotary = useMemo(() => {
    const grouped: Record<string, AvailableSlot[]> = {}
    slotsForDate.forEach(slot => {
      if (!grouped[slot.notary_id]) grouped[slot.notary_id] = []
      grouped[slot.notary_id].push(slot)
    })
    return grouped
  }, [slotsForDate])

  const handleConfirm = () => {
    if (!selectedSlot) return
    setIsSubmitting(true)
    router.post(`${basePath}/notarizations/${notarization.id}/appointments`, {
      appointment: {
        notary_id: selectedSlot.notary_id,
        scheduled_at: selectedSlot.scheduled_at,
        duration_minutes: selectedSlot.duration_minutes,
        timezone: selectedSlot.timezone,
      }
    }, { onFinish: () => setIsSubmitting(false) })
  }

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  const isDateAvailable = (date: Date) => {
    return availableDates.has(date.toDateString())
  }

  return (
    <DashboardLayout
      title="Book Appointment"
      breadcrumbs={[
        { label: 'Notarizations', href: 'notarizations' },
        { label: notarization.business_name, href: `notarizations/${notarization.id}` },
        { label: 'Book Appointment' }
      ]}
    >
      <div>
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <CalendarDays className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Book Appointment</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {notarization.type_name} · {notarization.session_mode === 'ron' ? 'Remote Online' : 'In Person'}
          </p>
        </div>

        <div className="space-y-6">
          {/* Step 1: Select Date */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">1</span>
              <h2 className="text-base font-medium text-foreground">Select a Date</h2>
            </div>
            {available_slots.length === 0 ? (
              <div className="border border-dashed rounded-lg py-10 text-center">
                <CalendarDays className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
                <h3 className="text-sm font-medium text-foreground mb-1">No available slots</h3>
                <p className="text-sm text-muted-foreground">There are no notaries available at this time. Please check back later.</p>
              </div>
            ) : (
              <div className="border rounded-lg p-4 flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => { setSelectedDate(date); setSelectedSlot(null) }}
                  disabled={(date) => !isDateAvailable(date) || date < new Date()}
                />
              </div>
            )}
          </section>

          {/* Step 2: Select Notary & Time */}
          {selectedDate && slotsForDate.length > 0 && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">2</span>
                <h2 className="text-base font-medium text-foreground">Select a Notary & Time</h2>
              </div>
              <div className="space-y-3">
                {Object.entries(slotsByNotary).map(([notaryId, slots]) => {
                  const notary = notaries.find(n => n.id === notaryId)
                  if (!notary) return null
                  return (
                    <div key={notaryId} className="border rounded-lg p-4">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center shrink-0">
                          <User className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm font-medium text-foreground">{notary.full_name}</h3>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                            <MapPin className="w-3 h-3" />
                            <span>{notary.commission_state}</span>
                          </div>
                          {notary.bio && (
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{notary.bio}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {slots.map((slot) => {
                          const isSelected = selectedSlot?.scheduled_at === slot.scheduled_at && selectedSlot?.notary_id === slot.notary_id
                          return (
                            <button
                              key={`${slot.notary_id}-${slot.scheduled_at}`}
                              type="button"
                              onClick={() => setSelectedSlot(slot)}
                              className={`px-3 py-1.5 text-xs rounded-md border transition-all ${
                                isSelected
                                  ? 'border-foreground bg-foreground text-background font-medium'
                                  : 'border-border hover:border-foreground/50 text-foreground'
                              }`}
                            >
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatTime(slot.scheduled_at)}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* Step 3: Confirm */}
          {selectedSlot && (
            <section>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground">3</span>
                <h2 className="text-base font-medium text-foreground">Confirm Booking</h2>
              </div>
              <div className="bg-muted rounded-xl p-4 space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Notary</span>
                    <span className="font-medium text-foreground">{selectedSlot.notary_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date</span>
                    <span className="text-foreground">
                      {new Date(selectedSlot.scheduled_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Time</span>
                    <span className="text-foreground">{formatTime(selectedSlot.scheduled_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground">{selectedSlot.duration_minutes} minutes</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Session Mode</span>
                    <span className="text-foreground flex items-center gap-1">
                      {notarization.session_mode === 'ron' ? <><Video className="w-3 h-3" /> Remote Online</> : <><MapPin className="w-3 h-3" /> In Person</>}
                    </span>
                  </div>
                </div>
                <Button onClick={handleConfirm} className="w-full h-9 text-sm" disabled={isSubmitting}>
                  {isSubmitting ? 'Booking...' : 'Confirm Appointment'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
